import { EXPLORER_PALETTE } from './fractalPalette';

// Flagship Celtic Dragon / Seahorse Julia set parameter: c = -0.8 + 0.156i
export const DEFAULT_JULIA_C = Object.freeze([-0.8, 0.156]);

// Vertical span is measured in complex-plane units. Derive horizontal span
// from the canvas proportions so both axes have the same units per pixel.
export function viewportFromCenter([x, y], verticalSpan, width, height) {
  const horizontalSpan = verticalSpan * width / height;
  return {
    xRange: [x - horizontalSpan / 2, x + horizontalSpan / 2],
    yRange: [y - verticalSpan / 2, y + verticalSpan / 2]
  };
}

function closeup(center, verticalSpan, height, gScale = 7, phaseOffset = 0, juliaC = DEFAULT_JULIA_C) {
  const width = 1600;
  const frozenJuliaC = Object.freeze([...juliaC]);
  return {
    width,
    height,
    juliaC: frozenJuliaC,
    ...viewportFromCenter(center, verticalSpan, width, height),
    palette: { ...EXPLORER_PALETTE, gScale, phaseOffset, juliaC: frozenJuliaC }
  };
}

export const closeups = {
  // Repelling alpha fixed point of the dragon Julia set c = -0.8 + 0.156i.
  seahorse: closeup([-0.52750312, 0.07591218], 0.006, 1200, 5, 0.32, [-0.8, 0.156]),
  // Seahorse Valley Spiral Sanctuary: 5.8e-3 vertical span around alpha_c of c = -0.747 + 0.108i.
  seahorseSpiralSanctuary: closeup([-0.49995812, 0.05400226], 0.0058, 1200, 5, 0.68, [-0.747, 0.108]),
  // Douady's Rabbit: superattracting period-3 bulb center c = -0.122561 + 0.744862i.
  douadyRabbit: closeup([0, 0], 2.1, 1200, 7, 0, [-0.1225611668766536, 0.7448617666197442]),
  // San Marco Basilica near the parabolic period-2 neck c = -0.75 + 0.015i.
  sanMarco: closeup([0, 0], 1.6, 800, 8, 0.15, [-0.75, 0.015]),
  // Golden Mean Siegel disk on the main cardioid boundary: rotation number (sqrt(5)-1)/2.
  siegelDisk: closeup([-0.12, 0.15], 1.65, 1200, 7, 0, [-0.390540870218399, 0.586787907346968]),
  // Double-spiral Julia medallion at 9e-6 span around alpha_c of c = -1.768620774 + 0.002428273i.
  juliaMedallion: closeup([-0.9207820045, 0.0008545551], 0.000009, 1200, 14, 0.06, [-1.768620774, 0.002428273]),
  // Elephant's Trunk Julia scroll around alpha_c of c = 0.2777323864 + 0.0073446267i.
  elephantTrunk: closeup([0.4781357360367159, 0.16795961557205044], 0.000022258585623343838, 1200, 8, 0.2, [0.2777323864244548, 0.0073446267400780795]),
  // Intertwined twin spiral arms at the origin of c = -0.7269 + 0.1889i.
  doubleSpiral: closeup([0, 0], 0.35, 1200, 5, 0.5, [-0.7269, 0.1889]),
  // Closer 4:3 framing of the repelling fixed-point core of c = -0.77568377 + 0.13646737i.
  doubleSpiralNexus: closeup([-0.51498922, 0.06722602], 0.0012, 1200, 14.21, 0.073, [-0.77568377, 0.13646737]),
  // Three-arm spiral junction around alpha_c of c = -0.087593732 + 0.65509028i.
  tripleSpiral: closeup([-0.23299236, 0.44686024], 0.0044, 1200, 7, 0, [-0.087593732, 0.65509028]),
  // Four-arm pinwheel around alpha_c of c = 0.28245 + 0.4831i.
  quadSpiral: closeup([0.02474291, 0.50825122], 0.004, 1200, 5, 0.5, [0.28245, 0.4831]),
  // Misiurewicz lightning dendrite at c = i with DEM filament enhancement.
  dendriteTree: (() => {
    const base = closeup([0, 0], 1.95, 1200, 8, 0, [0, 1]);
    return { ...base, palette: { ...base.palette, demStrength: 0.35 } };
  })(),
  // Scepter detail around alpha_c of c = -1.25066 + 0.02012i.
  scepter: closeup([-0.72504181, 0.00821196], 0.0009, 1200, 7, 0.56, [-1.25066, 0.02012]),
  // Snowflake rosette around alpha_c of c = -0.658448 - 0.466852i.
  snowflake: closeup([-0.48230179, -0.23763166], 0.00065, 1200, 6, 0.04, [-0.658448, -0.466852])
};

function deepCloseup({ centerX, centerY, spanY, precisionDigits, iterationBudget, height = 1200, gScale = 5, phaseOffset = 0.5, juliaC = DEFAULT_JULIA_C }) {
  const width = 1600;
  const frozenJuliaC = Object.freeze([...juliaC]);
  return {
    width,
    height,
    juliaC: frozenJuliaC,
    camera: {
      version: 1,
      centerX,
      centerY,
      spanY,
      aspect: width / height,
      precisionDigits,
    },
    ...(iterationBudget ? { iterationBudget } : {}),
    palette: { ...EXPLORER_PALETTE, gScale, phaseOffset, juliaC: frozenJuliaC },
  };
}

export const substructureSections = [
  {
    section: 'seahorse-valley',
    headingId: 'seahorse-valley-heading',
    title: 'Seahorse Dragon Vortex',
    description: 'Twin logarithmic spiral arms wind into the repelling \u03b1 fixed point of the dragon Julia set for c = \u22120.8 + 0.156i, centred near z = \u22120.5275 + 0.0759i.',
    preset: closeups.seahorse,
  },
  {
    section: 'seahorse-valley-spiral-sanctuary',
    headingId: 'seahorse-valley-spiral-sanctuary-heading',
    title: 'Seahorse Valley Spiral Sanctuary',
    description: 'Counter-rotating seahorse tails in the Julia set for c = \u22120.747 + 0.108i, centred on its repelling fixed point at a 5.8 \u00d7 10\u207b\u00b3 vertical span, separating deep nocturnal basins from luminous filigree curls.',
    preset: closeups.seahorseSpiralSanctuary,
  },
  {
    section: 'douadys-rabbit',
    headingId: 'douadys-rabbit-heading',
    title: 'Douady\u2019s Rabbit',
    description: 'For c \u2248 \u22120.122561 + 0.744862i at the centre of the period-3 bulb, three dark interior basins meet at every preimage of the \u03b1 fixed point to form the three-eared silhouette named by Adrien Douady.',
    preset: closeups.douadyRabbit,
  },
  {
    section: 'san-marco-basilica',
    headingId: 'san-marco-basilica-heading',
    title: 'San Marco Basilica',
    description: 'Near the period-2 basilica parameter c = \u22120.75 + 0.015i, pinched interior basins and mirrored spires stretch along the real axis like Saint Mark\u2019s Basilica reflected across a flooded Venetian lagoon.',
    preset: closeups.sanMarco,
  },
  {
    section: 'golden-mean-siegel-disk',
    headingId: 'golden-mean-siegel-disk-heading',
    title: 'Golden Mean Siegel Disk',
    description: 'On the main cardioid boundary at c \u2248 \u22120.390541 + 0.586788i, the fixed-point multiplier rotates by the golden mean. Instead of spiraling inward, bounded orbits trace invariant topological disks.',
    preset: closeups.siegelDisk,
  },
  {
    section: 'julia-medallion',
    headingId: 'julia-medallion-heading',
    title: 'Double-spiral Julia Medallion',
    description: 'At a 9 \u00d7 10\u207b\u2076 vertical span inside the Julia set for c = \u22121.768620774 + 0.002428273i, curling filaments around the repelling fixed point form paired logarithmic spirals and nested medallions.',
    preset: closeups.juliaMedallion,
  },
  {
    section: 'elephant-trunk',
    headingId: 'elephant-trunk-heading',
    title: 'Elephant\u2019s Trunk Scroll',
    description: 'Inside the Elephant Valley Julia set for c \u2248 0.277732 + 0.007345i near the main cardioid cusp, long curved branches curl into tightly wound parabolic trunks around z \u2248 0.478136 + 0.167960i.',
    preset: closeups.elephantTrunk,
  },
  {
    section: 'double-spiral-valley',
    headingId: 'double-spiral-heading',
    title: 'Double Spiral Filigree',
    description: 'In the Seahorse filigree Julia set for c = \u22120.7269 + 0.1889i, two intertwined arms wind across the origin while smaller curls repeat at every preimage of the critical orbit.',
    preset: closeups.doubleSpiral,
  },
  {
    section: 'double-spiral-nexus',
    headingId: 'double-spiral-nexus-heading',
    title: 'Double Spiral Nexus',
    description: 'Centred near z = \u22120.51498922 + 0.06722602i at a 1.2 \u00d7 10\u207b\u00b3 vertical span in the Julia set for c = \u22120.77568377 + 0.13646737i, paired logarithmic spirals converge on the repelling \u03b1 fixed point.',
    preset: closeups.doubleSpiralNexus,
  },
  {
    section: 'triple-spiral-valley',
    headingId: 'triple-spiral-heading',
    title: 'Triple Spiral Junction',
    description: 'For c = \u22120.087593732 + 0.65509028i near the upper period-3 bulb, three spiral branches meet at the repelling fixed point z \u2248 \u22120.232992 + 0.446860i and repeat along each tributary arm.',
    preset: closeups.tripleSpiral,
  },
  {
    section: 'quad-spiral-valley',
    headingId: 'quad-spiral-heading',
    title: 'Quad Spiral Pinwheel',
    description: 'Near the upper period-4 bulb at c = 0.28245 + 0.4831i, four-armed spiral filaments wind into the repelling fixed point z \u2248 0.024743 + 0.508251i, adding a fourth turn to the three-armed rabbit junction.',
    preset: closeups.quadSpiral,
  },
  {
    section: 'misiurewicz-dendrite',
    headingId: 'misiurewicz-dendrite-heading',
    title: 'Misiurewicz Lightning Dendrite',
    description: 'At the Misiurewicz parameter c = i, the critical orbit lands on a repelling cycle after two steps. With no attracting basin, the Julia set has empty interior and forms a pure, infinitely branched tree.',
    preset: closeups.dendriteTree,
  },
  {
    section: 'scepter-valley',
    headingId: 'scepter-heading',
    title: 'Scepter Horn Spiral',
    description: 'In the Scepter Valley Julia set for c = \u22121.25066 + 0.02012i, curled filaments around the repelling fixed point z \u2248 \u22120.725042 + 0.008212i sprout ornate branching spikes.',
    preset: closeups.scepter,
  },
  {
    section: 'snowflake',
    headingId: 'snowflake-heading',
    title: 'Snowflake Filigree Rosette',
    description: 'Fine, feathery branches in the Julia set for c = \u22120.658448 \u2212 0.466852i form crystalline snowflake clusters around z \u2248 \u22120.482302 \u2212 0.237632i.',
    preset: closeups.snowflake,
  },
];

export const extraDeepSubstructureSections = [
  {
    section: 'elephant-trunk-crown',
    headingId: 'elephant-trunk-crown-heading',
    title: 'Elephant\u2019s Trunk Filaments',
    description: 'At 10\u207b\u00b9\u2070 vertical span around the repelling fixed point of the Elephant Valley Julia set (c \u2248 0.277732 + 0.007345i), compensated double-single and perturbation passes resolve fine concentric rings around the spiral core.',
    preset: deepCloseup({
      centerX: '0.47813573603671586090',
      centerY: '0.16795961557205043046',
      spanY: '9.222493788438788563594384909335380003116512448175358920843214287e-11',
      precisionDigits: 128,
      juliaC: [0.2777323864244548, 0.0073446267400780795],
    }),
  },
  {
    section: 'seahorse-labyrinthine-spiral',
    headingId: 'seahorse-labyrinthine-spiral-heading',
    title: 'Seahorse Labyrinthine Spiral',
    description: 'At 1.2 \u00d7 10\u207b\u00b9\u2074 vertical span inside the Seahorse Valley Julia set (c = \u22120.77568377 + 0.13646737i), high-precision reference orbits and Bivariate Linear Approximation keep every spiral filament sharp near the IEEE 754 float64 precision floor.',
    preset: deepCloseup({
      centerX: '-0.5149892155705602964155198351415604',
      centerY: '0.0672260196987841907993338792619087',
      spanY: '1.2e-14',
      precisionDigits: 64,
      gScale: 18,
      phaseOffset: 0.47,
      juliaC: [-0.77568377, 0.13646737],
    }),
  },
];

export const parameterMorphPresets = [
  {
    section: 'cardioid-tour',
    headingId: 'cardioid-tour-heading',
    title: 'Cardioid Tour',
    description: 'Sweeping c(\u03b8) around the main cardioid boundary rotates the fixed-point multiplier \u03bb = re\u2071\u03b8 through a full turn, morphing the filled Julia set from the cusped cauliflower through three-eared rabbit buds, pinched basilica lobes, and golden-mean Siegel spirals.',
  },
  {
    section: 'bifurcation-pinch',
    headingId: 'bifurcation-pinch-heading',
    title: 'Bifurcation Pinch',
    description: 'Crossing the parabolic neck at c = \u22120.75 from the main cardioid (c = \u22120.52) into the period-2 basilica bulb (c = \u22121.0) and period-4 cascade (c = \u22121.31) pinches the single central basin at every preimage of the \u03b1 fixed point, splitting one chamber into an infinite chain of paired lobes.',
  },
  {
    section: 'dendrite-lightning',
    headingId: 'dendrite-lightning-heading',
    title: 'Dendrite Lightning',
    description: 'Tracing along the upper period-3 wake from Douady\u2019s Rabbit (c \u2248 \u22120.123 + 0.745i) out along the external ray filament to the Misiurewicz tip c = i collapses the interior basins to zero area, leaving a pure, infinitely branched lightning dendrite.',
  },
  {
    section: 'fatou-dust-transition',
    headingId: 'fatou-dust-transition-heading',
    title: 'Fatou Dust Transition',
    description: 'Crossing the Seahorse Valley boundary from a connected basin web (c = \u22120.745 + 0.105i) through the dragon filament (c = \u22120.7757 + 0.1365i) into the escaping basin (c = \u22120.805 + 0.225i) shatters the connected Julia continuum into an uncountable Fatou Cantor dust of isolated points.',
  },
];

export const selfSimilarZoomPresets = [
  {
    section: 'koenigs-self-similar-zoom',
    headingId: 'koenigs-self-similar-zoom-heading',
    title: 'Koenigs Self-Similar Spiral Loop',
    description: 'Near any repelling fixed point \u03b1 = (1 \u2212 \u221a(1 \u2212 4c))/2 with multiplier \u03bb = 2\u03b1 (|\u03bb| > 1), iterating m steps along the recurrence expands each spiral arm onto itself by \u03bb\u1d50. Applying the cubic inverse Koenigs chart \u03c8(w) = \u03b1 + w + a\u2082w\u00b2 + a\u2083w\u00b3 while scaling by |\u03bb|\u207b\u1d50\u1d57, counter-rotating by \u2212t\u00b7arg(\u03bb\u1d50), and shifting smooth escape counts by \u2212mt loops a single shallow octave seamlessly forever.',
  },
];

export const staticSubstructureSections = [
  ...substructureSections,
  ...extraDeepSubstructureSections,
];

export const allSubstructureSections = [
  ...staticSubstructureSections,
  ...parameterMorphPresets,
  ...selfSimilarZoomPresets,
];

function presetSpanX(preset) {
  if (preset.camera) {
    return Number(preset.camera.spanY) * (preset.camera.aspect || 1);
  }
  return preset.xRange[1] - preset.xRange[0];
}

export const OVERVIEW_SPAN_FLOOR = 1e-3;
export const DEEP_ZOOM_SPAN_FLOOR = 1e-16;

export const overviewPresets = staticSubstructureSections.filter(
  ({ preset }) => presetSpanX(preset) >= OVERVIEW_SPAN_FLOOR
);

export const deepZoomPresets = staticSubstructureSections.filter(({ preset }) => {
  const spanX = presetSpanX(preset);
  return spanX < OVERVIEW_SPAN_FLOOR && spanX >= DEEP_ZOOM_SPAN_FLOOR;
});
